import {
  Component,
  inject,
  input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  computed,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { WebsiteData } from '../../../../shared/models/website-overview.model';
import { MatCardModule } from '@angular/material/card';
import { OriginVhostService } from '../../services/origin-vhost.service';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  filter,
  forkJoin,
  map,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { PhpSettingsDialogComponent } from '../../dialogs/php-settings-dialog/php-settings-dialog.component';
import { SnackbarComponent } from '../../../../shared/components/snackbar/snackbar.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StatusModalService } from '../../../../core/services/status-modal.service';
import { WebsocketService } from '../../../../core/services/websocket.service';
import { ServersService } from '../../../../core/services/servers.service';

@Component({
  selector: 'app-php',
  imports: [MatCardModule, AsyncPipe, FontAwesomeModule, MatButtonModule],
  templateUrl: './php.component.html',
  styleUrl: './php.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhpComponent implements OnInit, OnDestroy {
  private originVhostService = inject(OriginVhostService);

  private serversService = inject(ServersService);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  private websocketService = inject(WebsocketService);

  destroyed$ = new Subject();

  refresh$ = new BehaviorSubject<void>(undefined);

  websiteData = input.required<WebsiteData>();

  websiteData$ = toObservable(this.websiteData);

  website = computed(() => this.websiteData().website);

  website$ = toObservable(this.website);

  originVhost$ = this.refresh$.pipe(
    switchMap(() => this.website$),
    switchMap((website) => this.originVhostService.getVhostByWebsiteId(website.id)),
    catchError((error) => {
      if (error.status === 404) {
        return EMPTY;
      }

      return throwError(() => error);
    }),
    shareReplay(1),
  );

  php$ = this.originVhost$.pipe(map((origin) => origin.php));

  configParameters$ = this.php$.pipe(
    map((php) =>
      php.config
        .map((config) => ({
          id: config.parameter.id,
          name: config.parameter.name,
          value: config.value.replace(/([kMG])$/, ' $1B').replace(/,/g, ', '),
        }))
        .sort((a, b) => a.id - b.id),
    ),
  );

  modules$ = this.php$.pipe(map((php) => php.modules.map((m) => m.name).join(', ')));

  server$ = this.websiteData$.pipe(
    switchMap(({ server }) => (server ? this.serversService.getServer(server.id) : of(null))),
    shareReplay(1),
  );

  ngOnInit(): void {
    this.websocketService
      .listen<{ title: string }>('notification')
      .pipe(
        takeUntil(this.destroyed$),
        filter(
          ({ title }) =>
            title === 'Origin Vhost PHP Configuration' || title === 'Origin Vhost Created!',
        ),
      )
      .subscribe(() => {
        this.refresh$.next();
      });
  }

  openSettingsDialog(): void {
    combineLatest({
      modules: this.originVhostService.getPHPModules(),
      parameters: this.originVhostService.getPHPConfigParameters(),
      php: this.php$.pipe(take(1)),
      server: this.server$.pipe(take(1)),
      aliases: this.originVhost$.pipe(
        map((vhost) => vhost.aliases.map((a) => a.alias)),
        take(1),
      ),
    })
      .pipe(
        switchMap(({ modules, parameters, php, server, aliases }) =>
          this.dialog
            .open(PhpSettingsDialogComponent, {
              data: {
                subTitle: this.website().domain,
                modules,
                parameters,
                php,
                server,
                aliases,
              },
              autoFocus: false,
            })
            .afterClosed(),
        ),
        filter((result) => !!result),
        withLatestFrom(this.originVhost$),
        switchMap(([{ modules, configs, phpUpdate }, origin]) => {
          const requests = {
            ...(modules &&
              configs && {
                config: this.originVhostService.updatePHPConfig(origin.id, {
                  modules,
                  configs,
                }),
              }),
            ...(phpUpdate && {
              version: this.originVhostService.updateVhost(origin.id, phpUpdate),
            }),
          };

          return forkJoin(requests);
        }),
      )
      .subscribe({
        next: () =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `PHP Settings update started`,
            },
          }),
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          });
        },
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
